import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import NotificationBell from '@/components/NotificationBell';
import apiService from '@/services/api';

// Navigation items - services categories loaded dynamically from API
const DEFAULT_NAV_LINKS = [
  { name: 'Home', path: '/', hasDropdown: false },
  { name: 'Services', path: '/services', hasDropdown: true },
  { name: 'How It Works', path: '/how-it-works', hasDropdown: false },
  { name: 'For Homeowners', path: '/for/owners', hasDropdown: false },
  { name: 'For Landlords & Agents', path: '/for/landlords', hasDropdown: false },
];

// AUDIT FIX: Service categories loaded from API (was previously hardcoded)
// Empty initially - populated from /api/public/service-categories API
const DEFAULT_SERVICE_CATEGORIES = [
  { name: 'All Services', href: '/services' },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS);
  const [serviceCategories, setServiceCategories] = useState(DEFAULT_SERVICE_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  // Track scroll for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch service categories from public API
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        const response = await apiService.get('/public/service-categories');
        if (response.success && Array.isArray(response.data)) {
          setServiceCategories([
            { name: 'All Services', href: '/services' },
            ...response.data.map((cat: any) => ({
              name: cat.name,
              href: `/services#${cat.slug || cat.id}`
            }))
          ]);
        }
      } catch (error) {
        console.error('Error fetching service categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServiceCategories();
  }, []);

  const isActive = (path: string) => {
    if (path === '/services') {
      return location.pathname.startsWith('/services');
    }
    return location.pathname === path;
  };

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="h-16 flex items-center px-4 animate-pulse bg-gray-100"></div>
      </header>
    );
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/80 backdrop-blur-md shadow-md border-b border-white/20'
        : 'bg-white border-b border-gray-200'
    }`}>
      <div className="h-16 flex items-center justify-center px-4">
        <div className="w-full max-w-6xl flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 min-w-max">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg">V</span>
            </div>
            <span className="font-bold text-teal-700 text-lg">Virewire</span>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center gap-8 flex-1">
            {navLinks.map((link) => {
              if (link.hasDropdown && link.name === 'Services') {
                return (
                  <DropdownMenu key={link.name}>
                    <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors data-[state=open]:text-teal-600">
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {serviceCategories.map((cat) => (
                        <DropdownMenuItem key={cat.href} asChild>
                          <Link to={cat.href} className="cursor-pointer">
                            {cat.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* Right side: Auth & CTA */}
          <div className="hidden lg:flex items-center gap-4 ml-auto">
            <NotificationBell />
            
            {isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline text-xs">{user.first_name || 'User'}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
                    {user.first_name} {user.last_name}
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to={
                      user.role === 'provider' ? '/provider/dashboard' :
                      user.role === 'admin' ? '/admin/dashboard' :
                      '/dashboard'
                    } className="cursor-pointer text-sm">
                      <User className="h-4 w-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/bookings" className="cursor-pointer text-sm">
                      My Bookings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-destructive cursor-pointer text-sm"
                    onClick={logout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" asChild className="text-gray-700 hover:text-teal-600">
                <Link to="/login" className="text-xs sm:text-sm">Sign In</Link>
              </Button>
            )}

            {/* Primary CTA */}
            <Button 
              size="sm" 
              asChild
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            >
              <Link to="/services">Request Service</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - moved outside h-16 container */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 py-4 animate-in slide-in-from-top">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => {
              if (link.name === 'Services') {
                return (
                  <div key={link.name}>
                    <Link
                      to="/services"
                      className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Services
                    </Link>
                    <div className="ml-4 border-l border-gray-200">
                      {serviceCategories.map((cat) => (
                        <Link
                          key={cat.href}
                          to={cat.href}
                          className="block px-4 py-2 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className="block px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}

            {/* Mobile Auth & CTA */}
            <div className="pt-4 mt-4 border-t border-gray-200 flex flex-col gap-2">
              {isAuthenticated && user ? (
                <>
                  <div className="px-4 py-2 text-xs font-medium text-gray-600">
                    Welcome, {user.first_name}
                  </div>
                  <Button variant="outline" size="sm" className="mx-4 border-gray-300" asChild>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <User className="h-4 w-4 mr-2" />
                      Dashboard
                    </Link>
                  </Button>
                  <Button variant="destructive" size="sm" className="mx-4" onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" className="mx-4 border-gray-300" asChild>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                </Button>
              )}

              {/* Primary CTA */}
              <Button
                size="sm"
                className="mx-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                asChild
              >
                <Link to="/service" onClick={() => setMobileMenuOpen(false)}>
                  Request a Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;